/* eslint-disable @typescript-eslint/naming-convention -- Schema.org requires @-prefixed keys. */
import type { Newsroom, NewsroomCompanyInformation, Story } from '@prezly/sdk';
import { getNewsroomFaviconUrl } from '@prezly/theme-kit-core';

import { getSocialLinks } from '@/components/SocialMedia/utils';

export type JsonLdSchema = Record<string, unknown>;

/** Escape script-breaking characters before putting JSON-LD in a script element. */
export function serializeJsonLd(schema: JsonLdSchema | JsonLdSchema[]): string {
    return JSON.stringify(schema).replace(/</g, '\\u003c');
}

function getHostname(url: string): string | undefined {
    try {
        return new URL(url).hostname.toLowerCase();
    } catch {
        return undefined;
    }
}

function getCompanyName(newsroom: Newsroom, companyInformation: NewsroomCompanyInformation) {
    return companyInformation.name || newsroom.name || newsroom.display_name;
}

function getDescription(companyInformation: NewsroomCompanyInformation) {
    return companyInformation.about?.replace(/<[^>]*>/g, '').trim();
}

function getPublisherSchema(
    newsroom: Newsroom,
    companyInformation: NewsroomCompanyInformation,
): JsonLdSchema {
    const sameAs = Object.values(getSocialLinks(companyInformation)).filter((url): url is string =>
        Boolean(url),
    );
    const logo = getNewsroomFaviconUrl(newsroom, 180);

    return {
        '@type': 'Organization',
        name: getCompanyName(newsroom, companyInformation),
        url: newsroom.url,
        ...(logo && { logo: { '@type': 'ImageObject', url: logo } }),
        ...(sameAs.length > 0 && { sameAs }),
    };
}

export function buildOrganizationSchema(
    newsroom: Newsroom,
    companyInformation: NewsroomCompanyInformation,
): JsonLdSchema {
    const description = getDescription(companyInformation);

    return {
        '@context': 'https://schema.org',
        ...getPublisherSchema(newsroom, companyInformation),
        ...(description && { description }),
    };
}

export function buildWebsiteSchema(
    newsroom: Newsroom,
    companyInformation: NewsroomCompanyInformation,
): JsonLdSchema {
    const name = getCompanyName(newsroom, companyInformation);
    const hostname = getHostname(newsroom.url);

    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name,
        url: newsroom.url,
        ...(hostname && hostname.toLowerCase() !== name.toLowerCase()
            ? { alternateName: hostname }
            : {}),
    };
}

export function buildNewsArticleSchema(
    story: Story,
    newsroom: Newsroom,
    companyInformation: NewsroomCompanyInformation,
): JsonLdSchema {
    const seoSettings = 'seo_settings' in story ? story.seo_settings : undefined;
    const headline = seoSettings?.meta_title || story.title;
    const description = seoSettings?.meta_description || story.subtitle || story.summary;
    const url = seoSettings?.canonical_url || story.oembed.url;

    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline,
        ...(description && { description }),
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        ...(story.oembed.thumbnail_url && {
            image: {
                '@type': 'ImageObject',
                url: story.oembed.thumbnail_url,
                ...(story.oembed.width && { width: story.oembed.width }),
                ...(story.oembed.height && { height: story.oembed.height }),
            },
        }),
        ...(story.published_at && { datePublished: story.published_at }),
        ...(story.updated_at && { dateModified: story.updated_at }),
        ...(story.author?.display_name && {
            author: { '@type': 'Person', name: story.author.display_name },
        }),
        publisher: getPublisherSchema(newsroom, companyInformation),
        inLanguage: story.culture.code,
    };
}
